import { ReactNode } from 'react';

import { CanvasElement, ElementStyle } from '@/features/canvas/types';
import { cn } from '@/components/ui/cn';
import { GLASS } from './glass';
import { ColorPicker } from '@/components/ui/ColorPicker';
import { Popover } from '@/components/ui/Popover';
import { MS, MS_ICON } from './ms';
import { cardFields, fieldOn } from './card-fields';

/** Sliders glyph for the "Fields" (configure shown data) control. */
const TuneIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className={className} aria-hidden="true">
    <line x1="4" y1="7" x2="20" y2="7" /><line x1="4" y1="17" x2="20" y2="17" />
    <circle cx="9" cy="7" r="2.4" fill="currentColor" stroke="none" /><circle cx="15" cy="17" r="2.4" fill="currentColor" stroke="none" />
  </svg>
);

/** Screen anchor for a floating toolbar: horizontal centre + the selection's top/bottom Y. */
export interface ToolbarAnchor {
  x: number;
  top: number;
  bottom: number;
}

const btn = 'flex h-8 min-w-8 items-center justify-center rounded-lg px-1 text-fg-muted transition-colors hover:bg-layer hover:text-fg';
const Sep = () => <span className="mx-0.5 h-5 w-px bg-line" />;

const alignIcon: Record<'left' | 'center' | 'right', ReactNode> = {
  left: <MS icon={MS_ICON.align_left} size={18} />,
  center: <MS icon={MS_ICON.align_center} size={18} />,
  right: <MS icon={MS_ICON.align_right} size={18} />,
};
const vAlignIcon: Record<'top' | 'middle' | 'bottom', ReactNode> = {
  top: <MS icon={MS_ICON.valign_top} size={18} />,
  middle: <MS icon={MS_ICON.valign_middle} size={18} />,
  bottom: <MS icon={MS_ICON.valign_bottom} size={18} />,
};

/**
 * The floating object toolbar — positioned just above (or below) the selection, Miro-style. Common
 * controls sit inline (colour, font size, bold, align); the rest live in a ⋮ overflow menu.
 */
export default function Inspector({
  selected,
  anchor,
  onColor,
  onFont,
  onBold,
  onAlign,
  onVAlign,
  onReorder,
  onDuplicate,
  onLock,
  onDelete,
  onChangeItem,
  onEditSearch,
  onSetField,
}: {
  selected: CanvasElement[];
  anchor: ToolbarAnchor | null;
  onColor: (c: string) => void;
  onFont: (delta: number) => void;
  onBold: () => void;
  onAlign: (a: 'left' | 'center' | 'right') => void;
  onVAlign: (a: 'top' | 'middle' | 'bottom') => void;
  onReorder: (dir: 'front' | 'back' | 'forward' | 'backward') => void;
  onDuplicate: () => void;
  onLock: () => void;
  onDelete: () => void;
  /** Re-target a single board-item card at a different backlog item. */
  onChangeItem?: () => void;
  /** Edit the SparkQL query of a single search-results card. */
  onEditSearch?: () => void;
  /** Toggle a data field's visibility on a single item / search card. */
  onSetField?: (key: string, on: boolean) => void;
}) {
  if (selected.length === 0 || !anchor) return null;
  const first = selected[0];
  const s: ElementStyle = first.style ?? {};
  const single = selected.length === 1 ? first : null;
  const isItemCard = single?.kind === 'item';
  const isSearchCard = single?.kind === 'search';
  const isCard = isItemCard || isSearchCard;
  const hasText = selected.some((e) => e.kind === 'sticky' || e.kind === 'text' || e.kind === 'shape');
  const locked = selected.every((e) => e.style?.locked);
  const align = s.textAlign ?? 'center';
  const valign = s.vAlign ?? 'middle';
  const swatch = first.color ?? (first.kind === 'sticky' ? '#fdd835' : '#4dabf7');
  // A single image shows its filename (no fill colour applies). Uploaded images carry `filename`; a
  // pasted/dropped URL falls back to the last path segment.
  const isImage = selected.length === 1 && first.kind === 'image';
  const imageName = s.filename || (s.src ? decodeURIComponent(s.src.split('/').pop()?.split('?')[0] || '') : '') || 'Image';

  // Place above the selection when there's room, else below. The gap clears the connection nub that sits
  // ~20px outside the edge (OUT 14 + its 6px radius) plus a small margin, so the nub stays fully visible
  // and doesn't collide with the toolbar.
  const NUB_GAP = 30;
  const above = anchor.top > 108;
  const top = above ? anchor.top - NUB_GAP : anchor.bottom + NUB_GAP;
  const translate = above ? 'translate(-50%, -100%)' : 'translate(-50%, 0)';

  return (
    <div
      data-wb-overlay
      onPointerDown={(e) => e.stopPropagation()}
      className={cn(GLASS, 'pointer-events-auto fixed z-30 flex items-center gap-0.5 rounded-2xl px-1.5 py-1.5')}
      style={{ left: anchor.x, top, transform: translate }}
    >
      {isCard ? (
        // Board-item / search-results cards: no fill colour applies — offer re-targeting instead.
        <button
          type="button"
          title={isItemCard ? 'Change which item this card shows' : 'Edit the search query'}
          onClick={isItemCard ? onChangeItem : onEditSearch}
          className={cn(btn, 'gap-1.5 px-2 text-[12.5px] font-medium')}
        >
          <MS icon={isItemCard ? MS_ICON.swap_horiz : MS_ICON.edit} size={16} className="text-fg-faint" />
          <span>{isItemCard ? 'Change item' : 'Edit search'}</span>
        </button>
      ) : isImage ? (
        <span className="flex max-w-[240px] items-center gap-1.5 px-1.5 text-[12px] text-fg-soft" title={imageName}>
          <MS icon={MS_ICON.image} size={15} className="shrink-0 text-fg-faint" />
          <span className="truncate">{imageName}</span>
        </span>
      ) : (
        <ColorPicker
          value={swatch}
          onChange={onColor}
          align="left"
          trigger={({ toggle }) => (
            <button type="button" title="Colour" onClick={toggle} className={btn}>
              <span className="h-5 w-5 rounded-full ring-1 ring-inset ring-black/25" style={{ background: swatch }} />
            </button>
          )}
        />
      )}
      {isCard && onSetField && single ? (
        <>
          <Sep />
          <Popover
            portal
            align="left"
            trigger={({ toggle }) => (
              <button type="button" title="Configure which data the card shows" onClick={toggle} className={cn(btn, 'gap-1.5 px-2 text-[12.5px] font-medium')}>
                <TuneIcon className="text-fg-faint" />
                <span>Fields</span>
              </button>
            )}
          >
            {() => (
              <div className="min-w-[176px] p-1 text-[13px] text-fg-soft">
                <div className="px-2 pb-1 pt-0.5 text-[11px] font-semibold uppercase tracking-wide text-fg-ghost">Show fields</div>
                {cardFields(single.kind).map((f) => {
                  const shown = fieldOn(single.style, f.key);
                  return (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => onSetField(f.key, !shown)}
                      className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left hover:bg-layer hover:text-fg"
                    >
                      <span className={cn('flex h-4 w-4 items-center justify-center rounded border', shown ? 'border-brand bg-brand text-white' : 'border-line text-transparent')}>
                        <MS icon={MS_ICON.check} size={12} />
                      </span>
                      <span className="flex-1">{f.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </Popover>
        </>
      ) : null}
      {hasText ? (
        <>
          <Sep />
          <button className={btn} title="Decrease font size" onClick={() => onFont(-2)} style={{ fontSize: 12 }}>A</button>
          <button className={btn} title="Increase font size" onClick={() => onFont(2)} style={{ fontSize: 17 }}>A</button>
          <button className={cn(btn, 'font-bold', s.bold && 'bg-brand text-white hover:bg-brand hover:text-white')} title="Bold" onClick={onBold} style={{ fontSize: 15 }}>
            B
          </button>
          <button className={btn} title="Text align" onClick={() => onAlign(align === 'left' ? 'center' : align === 'center' ? 'right' : 'left')}>
            {alignIcon[align]}
          </button>
          <button className={btn} title="Vertical align" onClick={() => onVAlign(valign === 'top' ? 'middle' : valign === 'middle' ? 'bottom' : 'top')}>
            {vAlignIcon[valign]}
          </button>
        </>
      ) : null}
      <Sep />
      <button className={cn(btn, locked && 'text-brand')} title={locked ? 'Unlock' : 'Lock'} onClick={onLock}>
        <MS icon={locked ? MS_ICON.lock : MS_ICON.lock_open} size={18} />
      </button>
      <Popover
        portal
        align="right"
        trigger={({ toggle }) => (
          <button type="button" title="More" onClick={toggle} className={btn}>
            <MS icon={MS_ICON.more_vert} size={18} />
          </button>
        )}
      >
        {(close) => (
          <div className="min-w-[184px] p-1 text-[13px] text-fg-soft">
            {[
              { label: 'Duplicate', hint: '⌘D', icon: <MS icon={MS_ICON.content_copy} size={16} />, act: onDuplicate },
              { label: 'Bring to front', icon: <MS icon={MS_ICON.flip_to_front} size={16} />, act: () => onReorder('front') },
              { label: 'Bring forward', act: () => onReorder('forward') },
              { label: 'Send backward', act: () => onReorder('backward') },
              { label: 'Send to back', icon: <MS icon={MS_ICON.flip_to_back} size={16} />, act: () => onReorder('back') },
            ].map((it) => (
              <button
                key={it.label}
                className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left hover:bg-layer hover:text-fg"
                onClick={() => {
                  it.act();
                  close();
                }}
              >
                <span className="flex h-4 w-4 items-center justify-center text-fg-faint">{it.icon ?? null}</span>
                <span className="flex-1">{it.label}</span>
                {it.hint ? <span className="text-[11px] text-fg-faint">{it.hint}</span> : null}
              </button>
            ))}
            <div className="my-1 h-px bg-line" />
            <button
              className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left text-danger hover:bg-danger/10"
              onClick={() => {
                onDelete();
                close();
              }}
            >
              <span className="flex h-4 w-4 items-center justify-center"><MS icon={MS_ICON.delete} size={16} /></span>
              <span className="flex-1">Delete</span>
              <span className="text-[11px] opacity-70">Del</span>
            </button>
          </div>
        )}
      </Popover>
    </div>
  );
}
